import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { UserContext } from '../user-provider/user-context';
import {
  NotificationsContext,
  NotificationsSetterContext,
  NotificationsStoreContext,
} from './notifications-context';
import { createExternalStore } from '@/shared/libs/create-external-store';
import { mutate } from 'swr';
import useSWRSubscription from 'swr/subscription';
import { useToast, TOAST_DURATION } from '@/shared/providers/toast-provider';
import { usePageVisitData } from '@/shared/hooks/usePageVisitData';
import { pushDataLayerEvent } from '@/shared/libs/analytics/gtm';
import { useLogout } from '../user-provider/use-user';
import { SessionLockOverlay } from './styles';
import { isDevAppEnv } from '@/shared/config/brand-by-target.mjs';

const INIT_DATA = {
  balance: null,
  transaction: null,
  modal: null,
};

const SUPPORT_REFRESH_DEBOUNCE_MS = 700;
const SUPPORT_DEDUP_WINDOW_MS = 2000;

const setSessionValueIfChanged = (key: string, value: string) => {
  if (sessionStorage.getItem(key) !== value) {
    sessionStorage.setItem(key, value);
  }
};

const processCriticalNotifications = (data) => {
  try {
    const messageData = JSON.parse(data);

    if (!Array.isArray(messageData) || messageData?.length === 0) {
      return;
    }

    const [type, value] = messageData;

    switch (type) {
      case 'paymentError':
        setSessionValueIfChanged('payment-error', 'true');
        break;

      case 'goldCard':
        setSessionValueIfChanged('gold-card', 'true');
        break;

      case 'bonus':
        // Cookies.set('bonusRecieved', true, { expires: 365 });
        break;

      case 'kycVerified':
        setSessionValueIfChanged('notification-kyc-verified', String(value));
        break;

      case 'popup':
        setSessionValueIfChanged('onboarding-popup', JSON.stringify(value));
        break;

      default:
        break;
    }
  } catch (e) {
    console.error('Failed to parse critical notification:', e);
  }
};

const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState(INIT_DATA);
  const [supportRefreshSignal, setSupportRefreshSignal] = useState(null);
  const [messageSignal, setMessageSignal] = useState<{
    sequence: number;
    raw: string;
  } | null>(null);
  const [isSessionLocked, setIsSessionLocked] = useState(false);
  const { user } = useContext(UserContext);
  const { logout } = useLogout();
  const [isNotifyLoading, setIsNotifyLoading] = useState(user?.isAuth);
  const storeRef = useRef<ReturnType<typeof createExternalStore> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createExternalStore({
      notifications,
      isNotifyLoading,
      supportRefreshSignal,
      messageSignal,
    });
  }

  // useLayoutEffect — иначе точечные селекторы (useBalance, useGoldCard, ...)
  // на кадр отставали бы от useNotifications() (тот обновляется в этом же рендере)
  useLayoutEffect(() => {
    storeRef.current!.setState({
      notifications,
      isNotifyLoading,
      supportRefreshSignal,
      messageSignal,
    });
  }, [notifications, isNotifyLoading, supportRefreshSignal, messageSignal]);
  const { showToast } = useToast();
  const { getPattern } = usePageVisitData();
  const supportRefreshTimerRef = useRef(null);
  const supportRefreshPendingRef = useRef(null);
  const supportDedupMapRef = useRef(new Map());
  const sessionLogoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queueSupportRefresh = useCallback((value) => {
    if (!value || value.type !== 'support') {
      return;
    }

    const link = typeof value.link === 'string' ? value.link : '';
    const dedupKey = `${value.type}|${link}`;
    const now = Date.now();
    const dedupMap = supportDedupMapRef.current;
    const lastTs = dedupMap.get(dedupKey);

    if (typeof lastTs === 'number' && now - lastTs < SUPPORT_DEDUP_WINDOW_MS) {
      return;
    }

    for (const [key, ts] of dedupMap) {
      if (now - ts >= SUPPORT_DEDUP_WINDOW_MS) {
        dedupMap.delete(key);
      }
    }

    dedupMap.set(dedupKey, now);
    supportRefreshPendingRef.current = {
      type: value.type,
      link,
    };

    if (supportRefreshTimerRef.current) {
      clearTimeout(supportRefreshTimerRef.current);
    }

    supportRefreshTimerRef.current = setTimeout(() => {
      const pending = supportRefreshPendingRef.current;
      if (!pending) {
        return;
      }

      setSupportRefreshSignal((prev) => ({
        seq: (prev?.seq || 0) + 1,
        type: pending.type,
        link: pending.link,
        ts: Date.now(),
      }));

      supportRefreshPendingRef.current = null;
      supportRefreshTimerRef.current = null;
    }, SUPPORT_REFRESH_DEBOUNCE_MS);
  }, []);

  const { data: message, error } = useSWRSubscription(
    user.isAuth ? 'notifications' : null,
    (key, { next }) => {
      const wsUrl = isDevAppEnv
        ? `${process.env.NEXT_PUBLIC_WEB_SOCKET_URL}/notify`
        : `wss://${window.location.host}/server/notify`;

      let isAlive = true;
      let socket: WebSocket | null | undefined = null;
      let reconnectTimeout: number | null = null;

      function connect() {
        if (!isAlive) return;

        socket = new WebSocket(wsUrl);

        socket.onopen = () => {
          const pattern = getPattern();
          socket?.send(pattern);
          setIsNotifyLoading(false);
        };

        socket.onmessage = (event) => {
          if (!isAlive) return;
          processCriticalNotifications(event.data);
          if (typeof event.data === 'string') {
            setMessageSignal((previous) => ({
              sequence: (previous?.sequence || 0) + 1,
              raw: event.data,
            }));
          }
          next(null, event.data);
        };

        socket.onerror = (error) => {
          setIsNotifyLoading(false);
          if (!isAlive) return;
          next(error);
        };

        socket.onclose = () => {
          socket = null;
          if (isAlive) {
            reconnectTimeout = setTimeout(() => {
              if (isAlive) {
                connect();
              }
            }, 2000);
          }
        };

        return socket;
      }

      socket = connect();

      return () => {
        isAlive = false;
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
        if (socket) {
          socket.onclose = null;
          socket.close();
          socket = null;
        }
      };
    }
  );

  useEffect(() => {
    if (!message) return;

    try {
      const messageData = JSON.parse(message);
      if (Array.isArray(messageData)) {
        const [type, value] = messageData;

        setNotifications((prev) => ({
          ...prev,
          [type]: value,
          ...(type === 'popup'
            ? {
                onboardingPopup:
                  typeof value === 'string' ? { type: value } : value,
              }
            : {}),
        }));

        if (type === 'analytics' && value && typeof value === 'object') {
          pushDataLayerEvent(value);
        }

        if (type === 'notify') {
          showToast(value);
          queueSupportRefresh(value);
          if (value.save) {
            mutate('/notifications/list');
          }
          if (value.type === 'bonus') {
            mutate('/profile/getBonuses');
          }
          if (value.type === 'system' && value.status === 'standard') {
            // Сессия на бэке уже мертва — блокируем UI на время показа тоста,
            // чтобы юзер не успел выполнить заведомо обречённое на 401 действие
            setIsSessionLocked(true);
            if (sessionLogoutTimeoutRef.current) {
              clearTimeout(sessionLogoutTimeoutRef.current);
            }
            sessionLogoutTimeoutRef.current = setTimeout(logout, TOAST_DURATION);
          }
        }
      }
    } catch (e) {
      console.error('Failed to parse message data:', e);
    }
  }, [message, queueSupportRefresh, showToast, logout]);

  useEffect(
    () => () => {
      if (supportRefreshTimerRef.current) {
        clearTimeout(supportRefreshTimerRef.current);
      }
      if (sessionLogoutTimeoutRef.current) {
        clearTimeout(sessionLogoutTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (error) {
      console.error('WebSocket error:', error);
      setIsNotifyLoading(false);
    }
  }, [error]);

  useEffect(() => {
    if (!user?.isAuth) {
      setIsSessionLocked(false);
      setMessageSignal(null);
    }
  }, [user?.isAuth]);

  const notificationsValue = useMemo(
    () => ({
      notifications,
      setNotifications,
      isNotifyLoading,
      supportRefreshSignal,
    }),
    [notifications, isNotifyLoading, supportRefreshSignal]
  );

  return (
    <NotificationsStoreContext.Provider value={storeRef.current}>
      <NotificationsSetterContext.Provider value={setNotifications}>
        <NotificationsContext.Provider value={notificationsValue}>
          <SessionLockOverlay $active={isSessionLocked} aria-hidden="true" />
          {children}
        </NotificationsContext.Provider>
      </NotificationsSetterContext.Provider>
    </NotificationsStoreContext.Provider>
  );
};

export { NotificationsProvider };
